import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import Api from 'entities/helpers/api';
import {
  LinkButton,
  Loading,
  ModalBody,
  ModalHeaderWithActions,
  NumericInput,
  ButtonTooltip,
  Link,
} from '@evertrue/et-components';
import FormField from 'components/forms/form-field';
import PropTypes from 'prop-types';
import DNAStore from 'base/dna/dna-store';
import EverTrue from 'app';
import { getLinkUrl } from 'entities/helpers/hardcodedLinks';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import Nav from 'components/lists/nav';
import NavItem from 'components/lists/nav-item';

const AddSolicitorGoalsModal = ({ contact = {}, close }) => {
  const [solicitorGoals, setSolicitorGoals] = useState({});
  const [loading, setLoading] = useState(false);
  const { flagEnabled: hasDXO } = useFeatureFlag('dxo');
  const [tab, setTab] = useState('solicitor');

  useEffect(() => {
    const loadGoals = async () => {
      try {
        setLoading(true);
        const result = await Api.GOALS.SOLICITOR.get({
          urlExtend: `/${contact.id}`,
          disableAlerts: true,
        });

        setSolicitorGoals(result);
        setLoading(false);
      } catch {
        setLoading(false);
      }
    };

    loadGoals();
  }, [contact.id]);

  const daysUntilEndOfFy = DNAStore.getDaysUntilFiscalYearEnd();

  const handleSubmit = async (values) => {
    setLoading(true);
    const solicitorGoalToSave = {
      solicitor_contact_id: contact.id,
      interactions: values.interactions,
      qualifications: values.qualifications,
      visits_total: values.visits_total,
      asks_made: values.asks_made,
      asks_approved: values.asks_approved,
      ask_amount_threshold: values.ask_amount_threshold,
      proposal_funded_amount: values.proposal_funded_amount,
      dxo_asks: values.dxo_asks,
      dxo_substantive_asks: values.dxo_substantive_asks,
    };

    try {
      await Api.GOALS.SOLICITOR.post({
        data: JSON.stringify(solicitorGoalToSave),
      });
      setLoading(false);
      EverTrue.Alert.success(`Success! Solicitor goal for ${contact.name} saved successfully.`);
      close();
    } catch (err) {
      setLoading(false);
      EverTrue.Alert.error(`Failure! Solicitor goal for ${contact.name} did not save.`);
    }
  };

  return (
    <div className="solicitor-goals-form--modal">
      <Formik initialValues={solicitorGoals} onSubmit={handleSubmit} enableReinitialize>
        {({ handleSubmit, setFieldValue, values, dirty }) => (
          <>
            <ModalHeaderWithActions
              title={`${contact.name} FY Goals`}
              closeModal={close}
              onSubmit={handleSubmit}
              disableSubmit={!dirty}
            />
            {loading && <Loading />}
            <ModalBody scrollable={false}>
              <div className="solicitor-goals-form--fiscal-year">
                <p className="solicitor-goals-form--fiscal-year-text">
                  <strong>FY GOALS </strong> {`(${daysUntilEndOfFy} days left in FY)`}
                </p>
              </div>
              {hasDXO && (
                <Nav
                  active={tab}
                  className="sub-nav"
                  onSelect={(selected_tab) => {
                    setTab(selected_tab);
                  }}
                >
                  <NavItem activeWhen="solicitor" label="Solicitor" />
                  <NavItem activeWhen="dxo" label="DXO" />
                </Nav>
              )}
              <div className="solicitor-goals-form--subtitle">Engagement</div>
              <div className="solicitor-goals-form--grid">
                <FormField inlineLabel={false} className="solicitor-goals-form--form-field" label="Interactions">
                  <NumericInput
                    value={values.interactions}
                    defaultValue=""
                    placeholder={0}
                    maskType="number"
                    onChange={(value) => setFieldValue('interactions', value)}
                  />
                </FormField>
                <FormField
                  inlineLabel={false}
                  className="solicitor-goals-form--form-field"
                  label={
                    <div>
                      Qualifications
                      <span data-testid="tooltip-icon">
                        <ButtonTooltip
                          tooltipPositionTop={true}
                          trigger={<i className="gt-icon gt-icon-info tooltip-icon" />}
                        >
                          The number of unique constituents with an interaction purpose marked as “Qualification” or
                          “Discovery”.
                        </ButtonTooltip>
                      </span>
                    </div>
                  }
                >
                  <NumericInput
                    value={values.qualifications}
                    defaultValue=""
                    placeholder={0}
                    maskType="number"
                    onChange={(value) => setFieldValue('qualifications', value)}
                  />
                </FormField>
                <FormField
                  inlineLabel={false}
                  className="solicitor-goals-form--form-field"
                  label={
                    <div>
                      Visits
                      <span data-testid="tooltip-icon">
                        <ButtonTooltip
                          tooltipPositionTop={true}
                          trigger={<i className="gt-icon gt-icon-info tooltip-icon" />}
                        >
                          The number of unique in-person or virtual meetings logged as interactions.
                        </ButtonTooltip>
                      </span>
                    </div>
                  }
                >
                  <NumericInput
                    value={values.visits_total}
                    defaultValue=""
                    placeholder={0}
                    maskType="number"
                    onChange={(value) => setFieldValue('visits_total', value)}
                  />
                </FormField>
              </div>
              {(!hasDXO || tab === 'solicitor') && (
                <>
                  <div className="solicitor-goals-form--subtitle">Proposals</div>
                  <div className="solicitor-goals-form--grid">
                    <FormField
                      inlineLabel={false}
                      className="solicitor-goals-form--form-field"
                      label={
                        <div>
                          Asks Made
                          <span data-testid="tooltip-icon">
                            <ButtonTooltip
                              tooltipPositionTop={true}
                              trigger={<i className="gt-icon gt-icon-info tooltip-icon" />}
                            >
                              The number of proposal asks made with an EverTrue-standardized status of “Pending
                              Response”, “Approved”, or “Declined”.
                            </ButtonTooltip>
                          </span>
                        </div>
                      }
                    >
                      <NumericInput
                        value={values.asks_made}
                        defaultValue=""
                        placeholder={0}
                        maskType="number"
                        onChange={(value) => setFieldValue('asks_made', value)}
                      />
                    </FormField>
                    <FormField
                      inlineLabel={false}
                      className="solicitor-goals-form--form-field"
                      label={
                        <div>
                          Asks Closed
                          <span data-testid="tooltip-icon">
                            <ButtonTooltip
                              tooltipPositionTop={true}
                              trigger={<i className="gt-icon gt-icon-info tooltip-icon" />}
                            >
                              The number of proposals funded with an EverTrue-standardized status of “Approved”.
                            </ButtonTooltip>
                          </span>
                        </div>
                      }
                    >
                      <NumericInput
                        value={values.asks_approved}
                        defaultValue=""
                        placeholder={0}
                        maskType="number"
                        onChange={(value) => setFieldValue('asks_approved', value)}
                        showIcon={true}
                      />
                    </FormField>
                    <FormField
                      inlineLabel={false}
                      className="solicitor-goals-form--form-field"
                      label={
                        <div>
                          Minimum Ask Amount
                          <span data-testid="tooltip-icon">
                            <ButtonTooltip
                              tooltipPositionTop={true}
                              trigger={<i className="gt-icon gt-icon-info tooltip-icon" />}
                            >
                              The minimum ask amount that qualifies for an Ask Made. Most organizations have a minimum
                              ask amount to count toward total asks (i.e. only counts when asks are 50K+)
                            </ButtonTooltip>
                          </span>
                        </div>
                      }
                    >
                      <NumericInput
                        value={values.ask_amount_threshold}
                        defaultValue=""
                        placeholder={0}
                        maskType="currency"
                        onChange={(value) => setFieldValue('ask_amount_threshold', value)}
                        showIcon={true}
                      />
                    </FormField>
                    <FormField
                      inlineLabel={false}
                      className="solicitor-goals-form--form-field"
                      label={
                        <div>
                          Ask Amount Closed
                          <span data-testid="tooltip-icon">
                            <ButtonTooltip
                              tooltipPositionTop={true}
                              trigger={<i className="gt-icon gt-icon-info tooltip-icon" />}
                            >
                              The dollar amount of proposals funded with an EverTrue-standardsized status of “Approved”.
                            </ButtonTooltip>
                          </span>
                        </div>
                      }
                    >
                      <NumericInput
                        value={values.proposal_funded_amount}
                        defaultValue=""
                        placeholder={0}
                        maskType="currency"
                        onChange={(value) => setFieldValue('proposal_funded_amount', value)}
                        showIcon={true}
                      />
                    </FormField>
                  </div>
                </>
              )}
              {hasDXO && tab === 'dxo' && (
                <>
                  <div className="solicitor-goals-form--subtitle">Asks</div>
                  <div className="solicitor-goals-form--grid">
                    <FormField
                      inlineLabel={false}
                      className="solicitor-goals-form--form-field"
                      label={
                        <div>
                          Asks Made
                          <span data-testid="tooltip-icon">
                            <ButtonTooltip
                              tooltipPositionTop={true}
                              trigger={<i className="gt-icon gt-icon-info tooltip-icon" />}
                            >
                              The number of passive and substantive asks tied to an interaction form with an interaction
                              type of “DXO Ask”.
                            </ButtonTooltip>
                          </span>
                        </div>
                      }
                    >
                      <NumericInput
                        value={values.dxo_asks}
                        defaultValue=""
                        placeholder={0}
                        maskType="number"
                        onChange={(value) => setFieldValue('dxo_asks', value)}
                      />
                    </FormField>
                    <FormField
                      inlineLabel={false}
                      className="solicitor-goals-form--form-field"
                      label={
                        <div>
                          Substantive Asks Made
                          <span data-testid="tooltip-icon">
                            <ButtonTooltip
                              tooltipPositionTop={true}
                              trigger={<i className="gt-icon gt-icon-info tooltip-icon" />}
                            >
                              The number of asks tied to an interaction form with an interaction type of “DXO Ask”,
                              where the substantive flag is checked.
                            </ButtonTooltip>
                          </span>
                        </div>
                      }
                    >
                      <NumericInput
                        value={values.dxo_substantive_asks}
                        defaultValue=""
                        placeholder={0}
                        maskType="number"
                        onChange={(value) => setFieldValue('dxo_substantive_asks', value)}
                      />
                    </FormField>
                  </div>
                </>
              )}
              <div className="journey-modal--alert">
                For more information about these metrics, please visit the{' '}
                <Link href={getLinkUrl('everTrue', 'forumUrl')} title="Evertrue Help Center" target="_blank">
                  Evertrue Help Center
                </Link>
              </div>
              <LinkButton
                className="solicitor-goals-form--clear-all"
                onClick={() => {
                  const solicitorFields = [
                    'interactions',
                    'qualifications',
                    'visits_total',
                    'asks_made',
                    'asks_approved',
                    'ask_amount_threshold',
                    'proposal_funded_amount',
                  ];

                  const dxoFields = [
                    'interactions',
                    'qualifications',
                    'visits_total',
                    'dxo_asks',
                    'dxo_substantive_asks',
                  ];

                  const fieldsToClear = hasDXO
                    ? tab === 'solicitor'
                      ? solicitorFields
                      : dxoFields
                    : [...solicitorFields];

                  fieldsToClear.forEach((key) => {
                    setFieldValue(key, '');
                  });
                }}
                title="Clear All"
              >
                Clear All
              </LinkButton>
            </ModalBody>
          </>
        )}
      </Formik>
    </div>
  );
};

AddSolicitorGoalsModal.propTypes = {
  close: PropTypes.func.isRequired,
  contact: PropTypes.object,
};

export default AddSolicitorGoalsModal;
